import {
  GetSsoProviderLoginUrlRequest,
  GetSsoProviderLoginUrlResponse,
  SsoProvider,
} from '@upright/common/iam/login/types';
import { clearLegacyAuthTokensFromStorage } from '@upright/ui/common/generic/utilities/storage';
import { useMutation } from '@tanstack/react-query';
import { fetchOrThrow } from '../../common/fetchers/fetch-or-throw';

async function getSsoProviderLoginUrl(payload: GetSsoProviderLoginUrlRequest) {
  return await fetchOrThrow<GetSsoProviderLoginUrlResponse>('/login/get-sso-provider-login-url', 'POST', payload);
}

// Once this request completes, the user is immediately redirected to perform SSO at the URL received from our server.
export function useGetSsoProviderLoginUrl(ssoProvider: SsoProvider, afterSuccessRedirectUrl: string) {
  return useMutation<GetSsoProviderLoginUrlResponse, Error>({
    mutationKey: ['get-sso-provider-login-url', ssoProvider],
    mutationFn: async () => {
      const payload: GetSsoProviderLoginUrlRequest = { ssoProvider, afterSuccessRedirectUrl };
      return await getSsoProviderLoginUrl(payload);
    },
    onSuccess: response => {
      window.location.assign(response.ssoProviderLoginUrl);
    },
  });
}

export async function removeTokenFromStorageAndForceNewSsoLogin(
  ssoProvider: SsoProvider,
  afterSuccessRedirectUrl?: string,
) {
  // First we clear any current login so that there won't be any failed requests after a refresh. Would be great
  // to also clear the user from the redux store, but that's not trivial to do from here.
  // PLT-4720: the authoritative auth state is now the HttpOnly `up-access`
  // cookie. We purge the legacy localStorage JWTs here as a defensive
  // cleanup; the cookie is invalidated server-side as soon as the SSO
  // redirect lands and the server issues a fresh `Set-Cookie` for the
  // re-authenticated session.
  clearLegacyAuthTokensFromStorage();

  // If no afterSuccessRedictUrl was gigen, we'll return the user to exactly the same URL where they currently are after the login. Any state
  // that's not stored in the URL will be lost, because the user will reload our whole app.
  const currentUrl = document.URL;
  const response = await getSsoProviderLoginUrl({
    ssoProvider,
    afterSuccessRedirectUrl: afterSuccessRedirectUrl || currentUrl,
    shouldForceNewLoginAtSsoProvider: true,
  });

  // Uncomment for debugging
  // if (!confirm(`Redirecting to ${response.ssoProviderLoginUrl}`)) return;
  window.location.assign(response.ssoProviderLoginUrl);
}
